import {
  DeclinePretestDocument,
  DeclinePretestMutation,
  LearnerPretestStatus,
  LearnerTrainingStatus,
  StartPretestDocument,
  StartPretestMutation,
  SubmitPretestDocument,
  SubmitPretestMutation,
  SubmitPretestMutationVariables,
  TrainingInitDocument,
  TrainingInitQuery
} from '~/data';
import { client } from '~/data/client';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { SliceCreator } from '../TrainingState';
import { Sequence } from '../navigation/NavigationSlice';
import {
  firstRequiredSequence,
  isInTestedOutChapter,
  withTestedOutChapters
} from '../navigation/requiredPath';
import { pretestTestedOutChapterIds, toPretest } from './pretest';
import { PretestSlice as Slice } from './PretestSlice';
import {
  clearPretestDraft,
  readPretestDraft,
  writePretestDraft
} from './pretestDraft';

// startPretest, submitPretest and declinePretest select the same fields.
type PretestLearnerTraining = NonNullable<
  NonNullable<SubmitPretestMutation['submitPretest']>['learnerTraining']
>;

interface PretestPayload {
  errors: string[];
  learnerTraining?: PretestLearnerTraining | null;
}

export const createPretestSlice: SliceCreator<Slice> = (set, get, api) => {
  let draftPretestId: string | null = null;
  /**
   * The pretest decides where the course starts and which chapters are on the
   * learner's path, so its result updates the outline as well.
   */
  const apply = (learnerTraining: PretestLearnerTraining) => {
    const state = get();
    const pretest = toPretest(learnerTraining.pretest);
    if (pretest?.status !== LearnerPretestStatus.InProgress) {
      clearPretestDraft(state.learnerTrainingId);
      draftPretestId = null;
    }
    const items = withTestedOutChapters(
      state.items,
      pretestTestedOutChapterIds(pretest)
    );
    const resume = items.find(
      (item): item is Sequence =>
        item.kind === 'sequence' &&
        item.chapterSequenceId === learnerTraining.chapterSequenceId
    );

    api.setState({
      pretest,
      items,
      // Answers only matter while the pretest is in progress.
      pretestAnswers:
        pretest?.status === LearnerPretestStatus.InProgress
          ? state.pretestAnswers
          : {},
      chapterSequenceId: learnerTraining.chapterSequenceId || '',
      highestUnlockedPosition: resume
        ? Math.max(state.highestUnlockedPosition, resume.position)
        : state.highestUnlockedPosition,
      // Leaving onboarding already moved the learner on in the player (see
      // next()); only a course the pretest finished changes the status here.
      status:
        learnerTraining.status === LearnerTrainingStatus.Completed
          ? LearnerTrainingStatus.Completed
          : state.status,
      // Pretest answers count toward the score. Read it the way next() does.
      scorePercent: learnerTraining.scorePercent ?? 0,
      lastAttemptScorePercent: learnerTraining.lastAttemptScorePercent ?? null,
      attemptStatus: learnerTraining.attemptStatus || '',
      attemptLimit: learnerTraining.attemptLimit ?? null,
      attemptsRemaining: learnerTraining.attemptsRemaining ?? null,
      ...(learnerTraining.scorePassingPercent != null
        ? { scorePassingPercent: learnerTraining.scorePassingPercent }
        : {}),
      ...(learnerTraining.certificateUrl
        ? { certificateUrl: learnerTraining.certificateUrl }
        : {}),
      ...(learnerTraining.featureShowCertificate
        ? { featureShowCertificate: learnerTraining.featureShowCertificate }
        : {})
    });
  };

  /**
   * Sends one pretest request at a time and applies its result, or tells the
   * learner why the server refused it.
   */
  const request = async (
    send: () => Promise<PretestPayload | null | undefined>
  ) => {
    if (get().pretestBusy) return false;
    set({ pretestBusy: true });

    try {
      const payload = await send();

      if (payload?.errors.length) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: payload.errors[0]
        });
        return false;
      }

      if (!payload?.learnerTraining) return false;

      apply(payload.learnerTraining);
      return true;
    } catch {
      // The error link already told the learner about network and API errors.
      return false;
    } finally {
      set({ pretestBusy: false });
    }
  };

  const pretestStatus = () => get().pretest?.status;

  return {
    pretestAnswers: {},
    pretestBusy: false,

    restorePretestAnswers: (pretestId, questions) => {
      if (draftPretestId === pretestId) return;
      draftPretestId = pretestId;
      set({
        pretestAnswers: readPretestDraft(
          get().learnerTrainingId,
          pretestId,
          questions
        )
      });
    },

    choosePretestOption: (questionId, optionId, multiple) => {
      if (get().pretestBusy) return;
      const chosen = get().pretestAnswers[questionId] ?? [];
      const isChosen = chosen.includes(optionId);

      let next: string[];
      if (multiple) {
        next = isChosen
          ? chosen.filter(id => id !== optionId)
          : [...chosen, optionId];
      } else {
        next = isChosen ? [] : [optionId];
      }

      const pretestAnswers = { ...get().pretestAnswers, [questionId]: next };
      set({ pretestAnswers });
      if (draftPretestId) {
        writePretestDraft(
          get().learnerTrainingId,
          draftPretestId,
          pretestAnswers
        );
      }
    },

    startPretest: async () => {
      const started = await request(async () => {
        const result = await client.mutate<StartPretestMutation>({
          mutation: StartPretestDocument,
          variables: { learnerTrainingUuid: get().learnerTrainingId }
        });
        return result.data?.startPretest;
      });

      return started && pretestStatus() === LearnerPretestStatus.InProgress;
    },

    // The server grades whatever the learner started on, so a submit only fails for a moment (network,
    // server or a conflicting write). The answers stay, for the learner to send again.
    submitPretest: async questionIds => {
      const { learnerTrainingId, pretestAnswers } = get();
      // Every drawn question is sent, answered or not: the server grades them.
      const answers: SubmitPretestMutationVariables['answers'] =
        questionIds.map(questionId => ({
          questionId,
          questionFieldIds: pretestAnswers[questionId] ?? []
        }));

      const submitted = await request(async () => {
        const result = await client.mutate<SubmitPretestMutation>({
          mutation: SubmitPretestDocument,
          variables: { learnerTrainingUuid: learnerTrainingId, answers }
        });
        return result.data?.submitPretest;
      });

      return submitted && pretestStatus() === LearnerPretestStatus.Completed;
    },

    declinePretest: async () => {
      const declined = await request(async () => {
        const result = await client.mutate<DeclinePretestMutation>({
          mutation: DeclinePretestDocument,
          variables: { learnerTrainingUuid: get().learnerTrainingId }
        });
        return result.data?.declinePretest;
      });

      return declined && pretestStatus() === LearnerPretestStatus.Declined;
    },

    // The course load reads the pretest with the same fields as the mutations.
    refreshPretest: () =>
      request(async () => {
        const result = await client.query<TrainingInitQuery>({
          query: TrainingInitDocument,
          variables: { learnerTrainingUuid: get().learnerTrainingId },
          fetchPolicy: 'network-only'
        });
        return { errors: [], learnerTraining: result.data.learnerTraining };
      }),

    pretestExitRoute: () => {
      const { status, items, chapterSequenceId } = get();

      // Testing out of every chapter finishes the course, as the last slide
      // would (see next()).
      if (status === LearnerTrainingStatus.Completed) return 'feedback';

      const resume = items.find(
        (item): item is Sequence =>
          item.kind === 'sequence' &&
          item.chapterSequenceId === chapterSequenceId
      );
      if (resume && !isInTestedOutChapter(items, resume)) return resume.id;

      // A retake has no resume point until its first slide is done.
      const first =
        firstRequiredSequence(items) ??
        items.find(item => item.kind === 'sequence');
      return first?.id ?? '';
    }
  };
};
